/**
 * BoardViewPage — page composition that composes existing isolated components
 * with ActiveBoardContext state, API integration, drag-and-drop card movement,
 * and real-time WebSocket updates.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 10.1, 10.2, 10.3, 10.4, 10.5, 10.6
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { ActiveBoardProvider, useActiveBoard, CardSummary, SwimlaneSummary, WebSocketEvent } from '../state/active-board-context';
import { ListColumn } from '../components/ListColumn';
import { SwimlaneRow } from '../components/SwimlaneRow';
import { MiniCard } from '../components/MiniCard';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { FilterBar, ActiveFilters } from '../components/FilterBar';
import { ActivitySidebar, ActivityEntry } from '../components/ActivitySidebar';
import { BoardSearch, SearchResult } from '../components/BoardSearch';
import { AdvancedSearchModal } from '../components/board/AdvancedSearchModal';
import { KeyboardCardMove, ListOption } from '../components/KeyboardCardMove';
import { ConnectionStatus } from '../components/ConnectionStatus';
import { InlineCreationForm } from '../components/InlineCreationForm';
import { ArchivedItemsPanel } from '../components/ArchivedItemsPanel';
import { BoardSettings } from '../components/BoardSettings';
import { ShareButton } from '../components/board/ShareButton';
import { JiraExportDialog } from '../components/board/JiraExportDialog';
import { WatchButton } from '../components/WatchButton';

import { ArchiveListDialog, ListOption as ArchiveListOption } from '../components/ArchiveListDialog';
import { ViewToggle } from '../components/board/ViewToggle';
import { OverflowMenu, OverflowMenuItem } from '../components/board/OverflowMenu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { CalendarView } from '../components/board/CalendarView';
import { MoveCardModal } from '../components/card/MoveCardModal';
import { createDragController, DragCallbacks, DragItem, DropTarget, DragState } from '../hooks/useDragAndDrop';
import { useLinkVisualization } from '../hooks/useLinkVisualization';
import { LinkOverlay } from '../components/LinkOverlay';
import { generateAriaDescription } from '../services/link-visual-attributes';
import * as api from '../services/api-client';
import { createWebSocketClient, WebSocketState, WebSocketClient } from '../services/websocket-client';
import { useAuth } from '../state/auth-context';
import { getTokens } from '../services/auth-client';
import { appConfig } from '../config';
import { getContrastMode } from '../utils/color-contrast';
import { PresenceAvatars } from '../components/PresenceAvatars';
import { useBreadcrumb } from '../state/breadcrumb-context';

// ---------------------------------------------------------------------------
// Page wrapper — provides ActiveBoardContext from URL params
// ---------------------------------------------------------------------------

interface BoardViewContentProps {
  readonly boardId: string;
  readonly getAccessToken: () => Promise<string | null>;
}

export function BoardViewPage(): React.ReactElement {
  const { boardId } = useParams<{ boardId: string }>();
  const { getAccessToken } = useAuth();

  if (!boardId) {
    return (
      <main aria-label="Board view">
        <p className="p-6 text-muted-foreground">No board selected.</p>
      </main>
    );
  }

  return (
    <ActiveBoardProvider boardId={boardId}>
      <BoardViewContent boardId={boardId} getAccessToken={getAccessToken} />
      <Outlet />
    </ActiveBoardProvider>
  );
}

// ---------------------------------------------------------------------------
// Internal content component — uses ActiveBoardContext
// ---------------------------------------------------------------------------

function BoardViewContent({ boardId, getAccessToken }: BoardViewContentProps): React.ReactElement {
  const { state, createCard, moveCard, fetchBoard, dispatch, reorderList, restoreCard, archiveList } = useActiveBoard();
  const navigate = useNavigate();
  const { setBoardTitle } = useBreadcrumb();

  // WebSocket connection state
  const [connectionState, setConnectionState] = useState<WebSocketState>('disconnected');
  const wsClientRef = useRef<WebSocketClient | null>(null);
  const previousStateRef = useRef<WebSocketState>('disconnected');

  // Stale board toast state
  const isEditingRef = useRef(false);
  const [showStaleToast, setShowStaleToast] = useState(false);
  const staleToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track whether user is actively editing (form open, text input focused)
  useEffect(() => {
    function handleFocusIn(e: FocusEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        isEditingRef.current = true;
      }
    }
    function handleFocusOut(e: FocusEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        isEditingRef.current = false;
      }
    }
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // WebSocket lifecycle — connect on mount, close on unmount
  // ---------------------------------------------------------------------------

  // Ref holding the current user ID so the (stable) WS event handler can filter
  // self-triggered events without needing to rebuild the socket when it changes.
  const currentUserIdRef = useRef<string>('unknown');

  useEffect(() => {
    // Skip WebSocket connection if no URL is configured (backend not yet deployed)
    if (!appConfig.wsUrl) return;

    let cancelled = false;

    // Resolves a fresh WS URL (with a fresh, non-expired token) for each connect
    // attempt. Returns null when the session can't be re-authenticated, which
    // tells the client to stop retrying and surface a session-expired state.
    const getUrl = async (): Promise<string | null> => {
      const token = await getAccessToken();
      if (!token) return null;
      return `${appConfig.wsUrl}?boardId=${encodeURIComponent(boardId)}&token=${encodeURIComponent(token)}`;
    };

    const client = createWebSocketClient({
      boardId,
      getUrl,
      createSocket: (url: string) => new WebSocket(url),
      onEvent: (event: Record<string, unknown>) => {
        // Read actorUserId off the raw event before the type-guard narrows it.
        const actorUserId = event['actorUserId'];
        if (!cancelled && isWebSocketEvent(event)) {
          // Skip events triggered by the current user (server-side also filters,
          // but this is defense-in-depth for race conditions)
          if (actorUserId === currentUserIdRef.current) return;

          if (isEditingRef.current) {
            setShowStaleToast(true);
            if (staleToastTimerRef.current) clearTimeout(staleToastTimerRef.current);
            staleToastTimerRef.current = setTimeout(() => setShowStaleToast(false), 10000);
          }
          dispatch({ type: 'WS_EVENT', payload: event });
        }
      },
      onStateChange: (wsState: WebSocketState) => {
        if (!cancelled) {
          setConnectionState(wsState);
        }
      },
      onRefetchNeeded: () => {
        if (!cancelled) fetchBoard(boardId);
      },
      onSessionExpired: () => {
        // Refresh token is gone/expired — send the user back to login to
        // re-establish a session (mirrors the REST layer's auth-failure flow).
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      },
    });

    wsClientRef.current = client;
    client.connect();

    return () => {
      cancelled = true;
      if (wsClientRef.current) {
        wsClientRef.current.disconnect();
        wsClientRef.current = null;
      }
      if (staleToastTimerRef.current) {
        clearTimeout(staleToastTimerRef.current);
      }
    };
  }, [boardId, getAccessToken, dispatch, fetchBoard]);

  // ---------------------------------------------------------------------------
  // Reconnection recovery — refetch board data when reconnecting → connected
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const prev = previousStateRef.current;
    previousStateRef.current = connectionState;

    if (prev === 'reconnecting' && connectionState === 'connected') {
      fetchBoard(boardId);
    }
  }, [connectionState, boardId, fetchBoard]);

  // ---------------------------------------------------------------------------
  // Wake-from-sleep / regained-connectivity trigger — proactively reconnect
  // (with a fresh token) instead of waiting out the backoff timer.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleWake = (): void => {
      if (document.visibilityState !== 'visible') return;
      wsClientRef.current?.reconnectNow();
    };
    document.addEventListener('visibilitychange', handleWake);
    window.addEventListener('online', handleWake);
    return () => {
      document.removeEventListener('visibilitychange', handleWake);
      window.removeEventListener('online', handleWake);
    };
  }, []);

  // Manual reconnect handler for ConnectionStatus
  const handleReconnect = useCallback(async (): Promise<void> => {
    wsClientRef.current?.reconnectNow();
  }, []);

  // Local UI state
  const [activeView, setActiveView] = useState<'list' | 'calendar'>('list');
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    labels: [],
    members: [],
    dueDateFilter: null,
    customFieldFilters: {},
  });


  // Link visualization
  const { state: linkVisualizationState, handlers: linkHandlers } = useLinkVisualization({ boardId });
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const [collapsedSwimlanes, setCollapsedSwimlanes] = useState<Set<string>>(new Set());
  const [showActivitySidebar, setShowActivitySidebar] = useState(false);
  const [activityEntries, setActivityEntries] = useState<ActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragItem: null,
    dropTarget: null,
    invalidTarget: false,
  });
  const [keyboardMoveCardId, setKeyboardMoveCardId] = useState<string | null>(null);
  const [showArchivedItems, setShowArchivedItems] = useState(false);
  const [showBoardSettings, setShowBoardSettings] = useState(false);
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [showJiraExport, setShowJiraExport] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedSearchQuery, setAdvancedSearchQuery] = useState('');
  const [archiveDialogListId, setArchiveDialogListId] = useState<string | null>(null);

  // Inline creation form state
  const [addingCardListId, setAddingCardListId] = useState<string | null>(null);
  const [addingCardSwimlaneId, setAddingCardSwimlaneId] = useState<string | null>(null);
  const [cardCreateError, setCardCreateError] = useState<string | null>(null);
  const [addingList, setAddingList] = useState<string | null>(null);
  const [listCreateError, setListCreateError] = useState<string | null>(null);
  const [addingSwimlane, setAddingSwimlane] = useState(false);
  const [swimlaneCreateError, setSwimlaneCreateError] = useState<string | null>(null);
  const [draggingListId, setDraggingListId] = useState<string | null>(null);

  // Fetch activity entries when sidebar opens
  useEffect(() => {
    if (!showActivitySidebar) return;
    let cancelled = false;

    async function fetchActivity(): Promise<void> {
      setActivityLoading(true);
      try {
        const response = await api.get<{ data: Array<{ activityId: string; actorName: string; actionType: string; targetId: string; timestamp: string }>; pagination: unknown }>(
          `/v1/boards/${boardId}/activity`,
        );
        if (!cancelled) {
          setActivityEntries(
            response.data.map((item) => ({
              activityId: item.activityId,
              actorName: item.actorName,
              actionType: item.actionType,
              targetName: item.targetId,
              timestamp: item.timestamp,
            })),
          );
        }
      } catch {
        if (!cancelled) {
          setActivityEntries([]);
        }
      } finally {
        if (!cancelled) {
          setActivityLoading(false);
        }
      }
    }

    fetchActivity();
    return () => { cancelled = true; };
  }, [showActivitySidebar, boardId]);

  // Cards currently in-flight (move API pending) — grey + non-draggable
  const [movingCardIds, setMovingCardIds] = useState<Set<string>>(new Set());

  // Drag controller ref — persists across renders
  const dragControllerRef = useRef<ReturnType<typeof createDragController> | null>(null);

  // Memoize drag callbacks
  const dragCallbacks: DragCallbacks = useMemo(() => ({
    onMoveCard: async (request) => {
      console.log('[DnD] onMoveCard called:', JSON.stringify(request));
      setMovingCardIds((prev) => new Set(prev).add(request.cardId));
      try {
        await moveCard(boardId, {
          cardId: request.cardId,
          targetListId: request.targetListId,
          targetSwimlaneId: request.targetSwimlaneId,
          sort: request.targetSort,
        });
        return { success: true };
      } catch (err) {
        console.error('[DnD] moveCard failed:', err);
        return { success: false };
      } finally {
        setMovingCardIds((prev) => {
          const next = new Set(prev);
          next.delete(request.cardId);
          return next;
        });
      }
    },
    onReorderList: async () => {
      // List reordering not wired in this task
      return { success: true };
    },
    checkWipLimit: async (listId: string) => {
      const list = state.lists.find((l) => l.listId === listId);
      if (!list || !list.wipEnabled || list.wipLimit === null) {
        return { allowed: true };
      }
      const cardCount = state.cards.filter((c) => c.listId === listId).length;
      if (list.wipMode === 'hard' && cardCount >= list.wipLimit) {
        return { allowed: false, error: 'WIP limit reached' };
      }
      return { allowed: true };
    },
  }), [boardId, moveCard, state.lists, state.cards]);

  // Initialize drag controller on first use
  const getDragController = useCallback(() => {
    if (!dragControllerRef.current) {
      dragControllerRef.current = createDragController(dragCallbacks);
    }
    return dragControllerRef.current;
  }, [dragCallbacks]);

  // Update drag controller when callbacks change
  React.useEffect(() => {
    dragControllerRef.current = null;
  }, [dragCallbacks]);

  // ---------------------------------------------------------------------------
  // Filter cards
  // ---------------------------------------------------------------------------

  // Get current user ID for assignee filter
  const currentUserId = useMemo(() => {
    const tokens = getTokens();
    if (!tokens?.accessToken) return 'unknown';
    try {
      const payload = tokens.accessToken.split('.')[1];
      if (!payload) return 'unknown';
      const decoded = JSON.parse(atob(payload));
      return (decoded.sub as string) ?? 'unknown';
    } catch {
      return 'unknown';
    }
  }, []);

  // Keep the ref in sync so the stable WS event handler filters self-events correctly.
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  // Derived: whether the current user is in read-only mode (non-member on public-read board)
  const isReadOnly = state.accessLevel === 'read-only' && !state.isMember;

  const filteredCards = useMemo(() => {
    return state.cards.filter((card) => {
      // Hide archived cards from the board view
      if ((card as unknown as Record<string, unknown>)['archived'] === true) {
        return false;
      }
      // Hide subtask cards unless toggle is on
      if (!showSubtasks && card.isSubtask) {
        return false;
      }
      if (activeFilters.labels.length > 0) {
        const cardLabelIds = card.labels.map((l) =>
          typeof l === 'string' ? l : (l as unknown as { labelId: string }).labelId
        );
        if (!activeFilters.labels.some((labelId) => cardLabelIds.includes(labelId))) {
          return false;
        }
      }
      if (activeFilters.members.length > 0) {
        if (!activeFilters.members.some((memberId) => card.members.some((m) => m.userId === memberId))) {
          return false;
        }
      }
      if (activeFilters.dueDateFilter === 'overdue') {
        if (!card.dueDate || new Date(card.dueDate).getTime() > Date.now()) {
          return false;
        }
      }
      if (activeFilters.dueDateFilter === 'no-due') {
        if (card.dueDate !== null) {
          return false;
        }
      }
      // Custom field filters — AND logic with all other filters
      const cfFilters = activeFilters.customFieldFilters;
      for (const [fieldId, filterValue] of Object.entries(cfFilters)) {
        if (filterValue === null) continue;
        const cardValue = card.customFields?.[fieldId];
        if (typeof filterValue === 'boolean') {
          // Boolean filter: card must have the exact boolean value
          if (cardValue !== filterValue) return false;
        } else if (Array.isArray(filterValue) && filterValue.length > 0) {
          // Dropdown filter: card value must include at least one of the selected options
          if (typeof cardValue === 'string') {
            if (!filterValue.includes(cardValue)) return false;
          } else if (Array.isArray(cardValue)) {
            if (!filterValue.some((fv) => cardValue.includes(fv))) return false;
          } else {
            // Card has no value for this field — doesn't match
            return false;
          }
        }
      }
      return true;
    });
  }, [state.cards, activeFilters, showSubtasks]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleCardClick = useCallback((cardId: string) => {
    if (cardId.startsWith('temp-')) return;
    navigate(`/boards/${boardId}/cards/${cardId}`);
  }, [navigate, boardId]);

  const handleCreateCard = useCallback(async (listId: string, swimlaneId: string) => {
    if (isReadOnly) return;
    setAddingCardListId(listId);
    setAddingCardSwimlaneId(swimlaneId);
    setCardCreateError(null);
  }, [isReadOnly]);

  const handleCreateCardSubmit = useCallback(async (title: string): Promise<void> => {
    if (!addingCardListId || !addingCardSwimlaneId) return;
    setCardCreateError(null);
    try {
      await createCard(boardId, { title, listId: addingCardListId, swimlaneId: addingCardSwimlaneId });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create card';
      setCardCreateError(message);
      throw error;
    }
  }, [boardId, createCard, addingCardListId, addingCardSwimlaneId]);

  const handleCreateCardCancel = useCallback(() => {
    setAddingCardListId(null);
    setAddingCardSwimlaneId(null);
    setCardCreateError(null);
  }, []);

  const handleCreateList = useCallback((swimlaneId?: string) => {
    setAddingList(swimlaneId ?? 'default');
    setListCreateError(null);
  }, []);

  const handleCreateListSubmit = useCallback(async (title: string): Promise<void> => {
    setListCreateError(null);
    try {
      await api.post(`/v1/boards/${boardId}/lists`, { title });
      setAddingList(null);
      await fetchBoard(boardId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create list';
      setListCreateError(message);
      throw error;
    }
  }, [boardId, fetchBoard]);

  const handleCreateListCancel = useCallback(() => {
    setAddingList(null);
    setListCreateError(null);
  }, []);

  const handleCreateSwimlane = useCallback(() => {
    setAddingSwimlane(true);
    setSwimlaneCreateError(null);
  }, []);

  const handleCreateSwimlaneSubmit = useCallback(async (title: string): Promise<void> => {
    setSwimlaneCreateError(null);
    // Calculate sort position to place at top
    const minSort = state.swimlanes
      .filter((s) => !s.archived)
      .reduce((min, s) => Math.min(min, s.sort), 0);
    const newSort = minSort - 1024;
    try {
      await api.post(`/v1/boards/${boardId}/swimlanes`, { title, position: newSort });
      setAddingSwimlane(false);
      await fetchBoard(boardId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create swimlane';
      setSwimlaneCreateError(message);
      throw error;
    }
  }, [boardId, fetchBoard, state.swimlanes]);

  const handleCreateSwimlaneCancel = useCallback(() => {
    setAddingSwimlane(false);
    setSwimlaneCreateError(null);
  }, []);

  const handleRenameSwimlane = useCallback(async (swimlaneId: string, newTitle: string) => {
    try {
      await api.put(`/v1/boards/${boardId}/swimlanes/${swimlaneId}`, { title: newTitle });
      fetchBoard(boardId);
    } catch {
      // Rename failed
    }
  }, [boardId, fetchBoard]);

  const handleRenameList = useCallback(async (listId: string, newTitle: string) => {
    try {
      await api.put(`/v1/boards/${boardId}/lists/${listId}`, { title: newTitle });
      fetchBoard(boardId);
    } catch {
      // Rename failed
    }
  }, [boardId, fetchBoard]);

  const handleArchiveSwimlane = useCallback(async (swimlaneId: string) => {
    try {
      await api.put(`/v1/boards/${boardId}/swimlanes/${swimlaneId}`, { archived: true });
      fetchBoard(boardId);
    } catch {
      // Archive failed
    }
  }, [boardId, fetchBoard]);

  const handleMoveSwimlane = useCallback(async (swimlaneId: string, direction: 'up' | 'down') => {
    const sorted = [...state.swimlanes].filter((s) => !s.archived).sort((a, b) => a.sort - b.sort);
    const index = sorted.findIndex((s) => s.swimlaneId === swimlaneId);
    if (index < 0) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;

    const current = sorted[index]!;
    const target = sorted[targetIndex]!;

    // Assign distinct sort values to ensure the swap takes effect
    // even if both currently have the same sort value
    const currentNewSort = target.sort !== current.sort ? target.sort : targetIndex * 1024;
    const targetNewSort = target.sort !== current.sort ? current.sort : index * 1024;

    try {
      // Sequential to avoid version conflicts if they somehow share a record
      await api.put(`/v1/boards/${boardId}/swimlanes/${current.swimlaneId}`, { sort: currentNewSort });
      await api.put(`/v1/boards/${boardId}/swimlanes/${target.swimlaneId}`, { sort: targetNewSort });
      fetchBoard(boardId);
    } catch {
      // Move failed — try refreshing anyway
      fetchBoard(boardId);
    }
  }, [boardId, state.swimlanes, fetchBoard]);

  const handleSetSprintDates = useCallback(async (swimlaneId: string, startDate: string | null, endDate: string | null) => {
    try {
      await api.put(`/v1/boards/${boardId}/swimlanes/${swimlaneId}`, { startDate, endDate });
      fetchBoard(boardId);
    } catch {
      // Sprint date update failed
    }
  }, [boardId, fetchBoard]);

  const handleToggleCollapse = useCallback((swimlaneId: string) => {
    setCollapsedSwimlanes((prev) => {
      const next = new Set(prev);
      if (next.has(swimlaneId)) {
        next.delete(swimlaneId);
      } else {
        next.add(swimlaneId);
      }
      return next;
    });
  }, []);

  const handleSearch = useCallback(async (query: string): Promise<SearchResult[]> => {
    const tokens = query.toLowerCase().split(/\s+/).filter((t) => t.length > 0);
    if (tokens.length === 0) return [];

    const boardLabels = state.board?.labels ?? [];
    const customFieldDefs = state.customFieldDefinitions;

    type ScoredResult = SearchResult & { score: number };
    const results: ScoredResult[] = [];

    for (const card of state.cards) {
      let score = 0;
      let matchContext: { field: 'title' | 'number' | 'label' | 'member' | 'custom-field'; value: string } | null = null;

      const titleLower = card.title.toLowerCase();
      const numberStr = String(card.cardNumber);

      // Title match — highest weight
      const titleTokenHits = tokens.filter((t) => titleLower.includes(t)).length;
      if (titleTokenHits > 0) {
        score += titleTokenHits * 10;
        if (!matchContext) matchContext = { field: 'title', value: card.title };
      }

      // Card number match
      if (tokens.some((t) => numberStr.includes(t))) {
        score += 8;
        if (!matchContext) matchContext = { field: 'number', value: `#${card.cardNumber}` };
      }

      // Label match
      for (const labelId of card.labels) {
        const label = boardLabels.find((l) => l.labelId === labelId);
        if (!label) continue;
        const labelLower = label.name.toLowerCase();
        if (tokens.some((t) => labelLower.includes(t))) {
          score += 5;
          if (!matchContext) matchContext = { field: 'label', value: label.name };
          break;
        }
      }

      // Member match
      for (const member of card.members) {
        const memberLower = member.displayName.toLowerCase();
        if (tokens.some((t) => memberLower.includes(t))) {
          score += 3;
          if (!matchContext) matchContext = { field: 'member', value: member.displayName };
          break;
        }
      }

      // Custom field match
      const cfValues = card.customFields ?? {};
      for (const [fieldId, value] of Object.entries(cfValues)) {
        if (typeof value === 'string' && value.length > 0) {
          if (tokens.some((t) => value.toLowerCase().includes(t))) {
            score += 2;
            const def = customFieldDefs.find((d) => d.fieldId === fieldId);
            if (!matchContext) matchContext = { field: 'custom-field', value: `${def?.name ?? 'Field'}: ${value}` };
            break;
          }
        }
        if (Array.isArray(value)) {
          const matched = value.find((v) => tokens.some((t) => v.toLowerCase().includes(t)));
          if (matched) {
            score += 2;
            const def = customFieldDefs.find((d) => d.fieldId === fieldId);
            if (!matchContext) matchContext = { field: 'custom-field', value: `${def?.name ?? 'Field'}: ${matched}` };
            break;
          }
        }
        if (typeof value === 'boolean' && value) {
          const def = customFieldDefs.find((d) => d.fieldId === fieldId);
          if (def && tokens.some((t) => def.name.toLowerCase().includes(t))) {
            score += 2;
            if (!matchContext) matchContext = { field: 'custom-field', value: def.name };
            break;
          }
        }
      }

      if (score > 0 && matchContext) {
        const list = state.lists.find((l) => l.listId === card.listId);
        const labelNames = card.labels
          .map((id) => boardLabels.find((l) => l.labelId === id)?.name)
          .filter((n): n is string => !!n);
        results.push({
          cardId: card.cardId,
          cardNumber: card.cardNumber,
          title: card.title,
          listName: list?.title ?? 'Unknown',
          labels: labelNames,
          matchContext,
          score,
        });
      }
    }

    // Sort by score descending, then by title alphabetically
    results.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));

    // Also query the server for description matches (fire-and-forget merge)
    try {
      const serverResults = await api.get<{ data: { cardId: string; cardNumber: number; title: string; listId: string; description: string; matchField: string; score: number }[] }>(
        `/v1/boards/${boardId}/search`,
        { q: query },
      );
      const serverData = serverResults.data ?? [];

      // Merge server results that aren't already in client results (description matches)
      const existingIds = new Set(results.map((r) => r.cardId));
      for (const sr of serverData) {
        if (existingIds.has(sr.cardId)) continue;
        const list = state.lists.find((l) => l.listId === sr.listId);
        results.push({
          cardId: sr.cardId,
          cardNumber: sr.cardNumber,
          title: sr.title,
          listName: list?.title ?? 'Unknown',
          labels: [],
          matchContext: {
            field: sr.matchField === 'description' ? 'custom-field' : 'title',
            value: sr.matchField === 'description' ? `Description: ${sr.description.slice(0, 60)}` : sr.title,
          },
          score: sr.score,
        });
      }
    } catch {
      // Server search failed — continue with client-side results only
    }

    // Re-sort after merging
    results.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));

    return results.slice(0, 20);
  }, [state.cards, state.lists, state.board?.labels, state.customFieldDefinitions, boardId]);

  // Drag-and-drop handlers
  const handleDragStart = useCallback((card: CardSummary) => {
    if (isReadOnly) return;
    const controller = getDragController();
    const item: DragItem = {
      id: card.cardId,
      type: 'card',
      sourceListId: card.listId,
      sourceSwimlaneId: card.swimlaneId,
      sourceSort: card.sort,
    };
    const newState = controller.startDrag(item);
    setDragState(newState);
  }, [getDragController, isReadOnly]);

  const handleDragOver = useCallback(async (target: DropTarget) => {
    const controller = getDragController();
    const newState = await controller.updateTarget(target);
    setDragState(newState);
  }, [getDragController]);

  const handleDrop = useCallback(async () => {
    const controller = getDragController();
    const currentState = controller.getState();
    console.log('[DnD] handleDrop called. dragItem:', JSON.stringify(currentState.dragItem), 'dropTarget:', JSON.stringify(currentState.dropTarget));
    const result = await controller.drop();
    console.log('[DnD] drop result:', JSON.stringify(result));
    setDragState({ isDragging: false, dragItem: null, dropTarget: null, invalidTarget: false });
  }, [getDragController]);

  const handleDragCancel = useCallback(() => {
    const controller = getDragController();
    controller.cancel();
    setDragState({ isDragging: false, dragItem: null, dropTarget: null, invalidTarget: false });
  }, [getDragController]);

  // Keyboard card move
  const handleKeyboardMove = useCallback(async (targetListId: string): Promise<{ success: boolean }> => {
    if (!keyboardMoveCardId) return { success: false };
    const card = state.cards.find((c) => c.cardId === keyboardMoveCardId);
    if (!card) return { success: false };
    try {
      await moveCard(boardId, {
        cardId: keyboardMoveCardId,
        targetListId,
        targetSwimlaneId: card.swimlaneId,
      });
      setKeyboardMoveCardId(null);
      return { success: true };
    } catch {
      return { success: false };
    }
  }, [keyboardMoveCardId, state.cards, boardId, moveCard]);

  // ---------------------------------------------------------------------------
  // List drag-and-drop handlers
  // ---------------------------------------------------------------------------

  const handleDragListStart = useCallback((listId: string) => {
    setDraggingListId(listId);
  }, []);

  const handleDragListOver = useCallback((_e: React.DragEvent, _targetListId: string) => {
    // Visual feedback is handled via CSS in the wrapper div
  }, []);

  const handleDropList = useCallback((targetListId: string) => {
    if (!draggingListId || draggingListId === targetListId) {
      setDraggingListId(null);
      return;
    }

    // Calculate the new sort position as midpoint between target's neighbors
    const sortedLists = [...state.lists].filter((l) => !l.archived).sort((a, b) => a.sort - b.sort);
    const targetIndex = sortedLists.findIndex((l) => l.listId === targetListId);
    const sourceIndex = sortedLists.findIndex((l) => l.listId === draggingListId);

    if (targetIndex < 0 || sourceIndex < 0) {
      setDraggingListId(null);
      return;
    }

    // Determine new sort: place the dragged item at the target position
    let newSort: number;
    if (sourceIndex < targetIndex) {
      // Moving right: place after the target
      const targetSort = sortedLists[targetIndex].sort;
      const nextSort = targetIndex + 1 < sortedLists.length ? sortedLists[targetIndex + 1].sort : targetSort + 1024;
      newSort = (targetSort + nextSort) / 2;
    } else {
      // Moving left: place before the target
      const targetSort = sortedLists[targetIndex].sort;
      const prevSort = targetIndex - 1 >= 0 ? sortedLists[targetIndex - 1].sort : targetSort - 1024;
      newSort = (prevSort + targetSort) / 2;
    }

    // Optimistic update via context and API call
    reorderList(boardId, draggingListId, newSort);
    setDraggingListId(null);
  }, [draggingListId, state.lists, boardId, reorderList]);

  const handleMoveList = useCallback((listId: string, direction: 'left' | 'right') => {
    const sortedLists = [...state.lists].filter((l) => !l.archived).sort((a, b) => a.sort - b.sort);
    const idx = sortedLists.findIndex((l) => l.listId === listId);
    if (idx < 0) return;

    let newSort: number;
    if (direction === 'left' && idx > 0) {
      const prevSort = sortedLists[idx - 1]!.sort;
      const beforePrevSort = idx > 1 ? sortedLists[idx - 2]!.sort : prevSort - 1024;
      newSort = Math.floor((beforePrevSort + prevSort) / 2);
    } else if (direction === 'right' && idx < sortedLists.length - 1) {
      const nextSort = sortedLists[idx + 1]!.sort;
      const afterNextSort = idx < sortedLists.length - 2 ? sortedLists[idx + 2]!.sort : nextSort + 1024;
      newSort = Math.floor((nextSort + afterNextSort) / 2);
    } else {
      return;
    }

    reorderList(boardId, listId, newSort);
  }, [state.lists, boardId, reorderList]);

  // Reset dragging state if drag ends without a drop
  useEffect(() => {
    function handleDragEnd() {
      setDraggingListId(null);
    }
    document.addEventListener('dragend', handleDragEnd);
    return () => document.removeEventListener('dragend', handleDragEnd);
  }, []);

  // ---------------------------------------------------------------------------
  // Link visualization — aria-description on active card element
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const { activeCardId: activeId, links: activeLinks } = linkVisualizationState;
    if (!activeId || activeLinks.length === 0) {
      // Remove aria-description from any card that might still have it
      const described = document.querySelector('[aria-description]');
      if (described) {
        described.removeAttribute('aria-description');
      }
      return;
    }

    const cardEl = document.querySelector(`[data-card-id="${activeId}"]`);
    if (cardEl) {
      const description = generateAriaDescription(activeLinks);
      cardEl.setAttribute('aria-description', description);
    }

    return () => {
      if (cardEl) {
        cardEl.removeAttribute('aria-description');
      }
    };
  }, [linkVisualizationState]);

  // ---------------------------------------------------------------------------
  // Keyboard shortcut for card move (M key)
  // ---------------------------------------------------------------------------

  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'm' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement;
        // Only trigger when focus is on a card element
        if (target.closest('[data-card-id]')) {
          e.preventDefault();
          const cardId = target.closest('[data-card-id]')?.getAttribute('data-card-id');
          if (cardId) {
            setKeyboardMoveCardId(cardId);
          }
        }
      }
      if (e.key === 'Escape' && keyboardMoveCardId) {
        setKeyboardMoveCardId(null);
      }
      // Ctrl+Shift+F / Cmd+Shift+F → open Advanced Search
      if (e.key === 'F' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault();
        setShowAdvancedSearch(true);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [keyboardMoveCardId]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const listOptions: ListOption[] = useMemo(() =>
    state.lists
      .filter((l) => !l.archived)
      .sort((a, b) => a.sort - b.sort)
      .map((l) => ({ listId: l.listId, title: l.title })),
    [state.lists]
  );

  const filterLabels = useMemo(() =>
    state.board?.labels.map((l) => ({ labelId: l.labelId, name: l.name })) ?? [],
    [state.board?.labels]
  );

  const filterMembers = useMemo(() =>
    state.board?.members.map((m) => ({ userId: m.userId, displayName: m.displayName })) ?? [],
    [state.board?.members]
  );

  const keyboardMoveCard = state.cards.find((c) => c.cardId === keyboardMoveCardId);

  // Set board title for breadcrumb display (reactive via context)
  useEffect(() => {
    if (state.board?.title) {
      setBoardTitle(state.board.title);
    }
    return () => { setBoardTitle(null); };
  }, [state.board?.title, setBoardTitle]);

  // Overflow menu items — secondary toolbar actions
  const overflowMenuItems = useMemo((): OverflowMenuItem[] => {
    const items: OverflowMenuItem[] = [];
    if (activeView === 'calendar') {
      items.push({
        id: 'break-out',
        label: 'Break Out Calendar',
        onClick: () => {
          const popup = window.open(`/boards/${boardId}/calendar`, '_blank');
          if (popup) setActiveView('list');
        },
      });
    }
    items.push({
      id: 'activity',
      label: 'Activity',
      active: showActivitySidebar,
      onClick: () => setShowActivitySidebar((v) => !v),
    });
    items.push({
      id: 'archived',
      label: 'Archived',
      active: showArchivedItems,
      onClick: () => setShowArchivedItems((v) => !v),
    });
    items.push({
      id: 'subtasks',
      label: 'Subtasks',
      active: showSubtasks,
      onClick: () => setShowSubtasks((v) => !v),
    });
    if (!isReadOnly) {
      items.push({
        id: 'swimlane',
        label: '+ Swimlane',
        onClick: handleCreateSwimlane,
      });
    }
    items.push({
      id: 'jira-export',
      label: 'Export to Jira',
      onClick: () => setShowJiraExport(true),
    });
    items.push({
      id: 'settings',
      label: 'Settings',
      active: showBoardSettings,
      onClick: () => setShowBoardSettings((v) => !v),
    });
    return items;
  }, [activeView, boardId, showActivitySidebar, showArchivedItems, showSubtasks, showBoardSettings, handleCreateSwimlane, isReadOnly]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (state.isLoading && !state.board) {
    return (
      <main aria-label="Board view" className="flex items-center justify-center h-full">
        <p className="text-muted-foreground" role="status" aria-live="polite">Loading board...</p>
      </main>
    );
  }

  if (state.error && !state.board) {
    return (
      <main aria-label="Board view" className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-destructive" role="alert">{state.error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Retry
        </button>
      </main>
    );
  }

  if (!state.board) {
    return (
      <main aria-label="Board view">
        <p className="p-6 text-muted-foreground">Board not found.</p>
      </main>
    );
  }

  const sortedLists = [...state.lists].filter((l) => !l.archived).sort((a, b) => a.sort - b.sort);
  const sortedSwimlanes = [...state.swimlanes].filter((s) => !s.archived).sort((a, b) => a.sort - b.sort);
  const hasSwimlanes = sortedSwimlanes.length > 1;

  // Determine text color based on board background for WCAG AA contrast
  const boardColor = state.board.color || '#1d4ed8';

  const contrastMode = getContrastMode(boardColor);
  const isLightText = contrastMode === 'light';
  const textColorClass = isLightText ? 'text-white' : 'text-gray-900';
  const textMutedClass = isLightText ? 'text-white/80' : 'text-gray-900/70';
  const borderClass = isLightText ? 'border-white/30' : 'border-gray-900/30';
  const hoverBgClass = isLightText ? 'hover:bg-white/20' : 'hover:bg-black/10';
  const ringClass = isLightText ? 'focus:ring-white' : 'focus:ring-gray-900';

  return (
    <main aria-label={`Board: ${state.board.title}`} className="flex flex-col h-full">
      {/* Board header */}
      <header
        className="px-4 py-3 flex items-center justify-between relative z-20"
        style={{ backgroundColor: boardColor }}
      >
        {/* Dark-mode brightness reduction overlay */}
        <div className="absolute inset-0 bg-black/20 hidden dark:block pointer-events-none" aria-hidden="true" />
        <div className="relative z-10 flex items-center justify-between w-full gap-4">
          <div className="flex items-baseline gap-3 min-w-0 shrink">
            <h1 className={`text-xl font-semibold ${textColorClass} shrink-0 whitespace-nowrap`}>{state.board.title}</h1>
            {state.board.description && (
              <p className={`text-sm ${textMutedClass} truncate hidden lg:block`} title={state.board.description}>{state.board.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-44">
              <BoardSearch onSearch={handleSearch} onCardSelect={handleCardClick} onAdvancedClick={(q) => { setAdvancedSearchQuery(q); setShowAdvancedSearch(true); }} />
            </div>
            <span className={`text-sm ${textMutedClass} hidden md:inline`}>
              {state.board.members.length} member{state.board.members.length !== 1 ? 's' : ''}
            </span>
            <PresenceAvatars endpoint={`/v1/boards/${boardId}/viewers`} />
            <ShareButton
              boardId={boardId}
              currentUserId={currentUserId}
              currentUserRole={(state.board.members.find((m) => m.userId === currentUserId)?.role as 'admin' | 'normal' | 'read-only') ?? 'read-only'}
              onMembershipChanged={() => fetchBoard(boardId)}
              currentVisibility={state.board.visibility}
              className={`${textColorClass} px-3 py-1 rounded border ${borderClass} ${hoverBgClass} focus:outline-none focus:ring-2 ${ringClass} inline-flex items-center gap-1.5`}
            />
            <WatchButton resourceType="board" resourceId={boardId} />
            <ViewToggle activeView={activeView} onViewChange={setActiveView} />
            <OverflowMenu
              items={overflowMenuItems}
              buttonClassName={`${textColorClass} px-3 py-1 rounded border ${borderClass} ${hoverBgClass} focus:outline-none focus:ring-2 ${ringClass} text-lg leading-none`}
            />
          </div>
        </div>
      </header>

      {/* Read-only banner for non-members on public-read boards */}
      {state.accessLevel === 'read-only' && !state.isMember && (
        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-950 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm flex items-center gap-2">
          <span aria-hidden="true">👁</span>
          <span>You are viewing this board in read-only mode.</span>
        </div>
      )}

      {/* Toolbar: Filter — only show when there are labels or multiple members to filter by */}
      {(filterLabels.length > 0 || filterMembers.length > 1) && (
        <div className="flex items-center gap-4 px-4 py-2 relative z-10" style={{ backgroundColor: boardColor }}>
          <div className="absolute inset-0 bg-black/20 hidden dark:block pointer-events-none" aria-hidden="true" />
          <div className="relative flex items-center gap-3">
            <FilterBar
              labels={filterLabels}
              members={filterMembers}
              activeFilters={activeFilters}
              onFilterChange={setActiveFilters}
              currentUserId={currentUserId}
              customFieldDefinitions={state.customFieldDefinitions}
            />
          </div>
        </div>
      )}

      {/* Board content */}
      {activeView === 'calendar' ? (
        <div className="flex flex-1 overflow-hidden relative" style={{ backgroundColor: boardColor }}>
          <div className="absolute inset-0 bg-black/20 hidden dark:block pointer-events-none" aria-hidden="true" />
          <div className="flex-1 relative z-10">
            <CalendarView
              cards={filteredCards}
              lists={state.lists}
              boardId={boardId}
              isStandalone={false}
            />
          </div>

          {/* Activity Sidebar */}
          {showActivitySidebar && (
            <div className="w-80 border-l border-border overflow-y-auto bg-card relative z-10">
              <ActivitySidebar entries={activityEntries} isLoading={activityLoading} onClose={() => setShowActivitySidebar(false)} />
            </div>
          )}
        </div>
      ) : (
      <div className="flex flex-1 overflow-hidden relative" style={{ backgroundColor: boardColor }}>
        {/* Dark-mode brightness reduction overlay for board content area */}
        <div className="absolute inset-0 bg-black/20 hidden dark:block pointer-events-none" aria-hidden="true" />
        <div
          ref={boardScrollRef}
          className="flex-1 overflow-y-auto overflow-x-scroll p-4 relative z-10"
          role="region"
          aria-label="Board content"
          onClick={(e) => linkHandlers.onBoardClick(e.nativeEvent)}
        >
          <ErrorBoundary label="board">
          {hasSwimlanes ? (
            <BoardWithSwimlanes
              swimlanes={sortedSwimlanes}
              lists={sortedLists}
              cards={filteredCards}
              collapsedSwimlanes={collapsedSwimlanes}
              onToggleCollapse={handleToggleCollapse}
              onCardClick={handleCardClick}
              onCreateCard={handleCreateCard}
              onCreateCardSubmit={handleCreateCardSubmit}
              onCreateCardCancel={handleCreateCardCancel}
              addingCardListId={addingCardListId}
              addingCardSwimlaneId={addingCardSwimlaneId}
              cardCreateError={cardCreateError}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragCancel={handleDragCancel}
              dragState={dragState}
              contrastMode={contrastMode}
              movingCardIds={movingCardIds}
              onCreateSwimlane={handleCreateSwimlane}
              onCreateSwimlaneSubmit={handleCreateSwimlaneSubmit}
              onCreateSwimlaneCancel={handleCreateSwimlaneCancel}
              addingSwimlane={addingSwimlane}
              swimlaneCreateError={swimlaneCreateError}
              showSubtasks={showSubtasks}
              onArchiveList={(listId) => setArchiveDialogListId(listId)}
              onMoveList={handleMoveList}
              onRenameList={handleRenameList}
              onRenameSwimlane={handleRenameSwimlane}
              onArchiveSwimlane={handleArchiveSwimlane}
              onMoveSwimlane={handleMoveSwimlane}
              onSetSprintDates={handleSetSprintDates}
              onCreateList={handleCreateList}
              onCreateListSubmit={handleCreateListSubmit}
              onCreateListCancel={handleCreateListCancel}
              addingList={addingList}
              listCreateError={listCreateError}
              onCardMouseEnter={linkHandlers.onCardMouseEnter}
              onCardMouseLeave={linkHandlers.onCardMouseLeave}
            />
          ) : (
            <BoardFlatLayout
              lists={sortedLists}
              cards={filteredCards}
              swimlaneId={sortedSwimlanes[0]?.swimlaneId ?? 'default'}
              onCardClick={handleCardClick}
              onCreateCard={handleCreateCard}
              onCreateCardSubmit={handleCreateCardSubmit}
              onCreateCardCancel={handleCreateCardCancel}
              addingCardListId={addingCardListId}
              addingCardSwimlaneId={addingCardSwimlaneId}
              cardCreateError={cardCreateError}
              onCreateList={handleCreateList}
              onCreateListSubmit={handleCreateListSubmit}
              onCreateListCancel={handleCreateListCancel}
              addingList={addingList}
              listCreateError={listCreateError}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragCancel={handleDragCancel}
              dragState={dragState}
              contrastMode={contrastMode}
              onDragListStart={handleDragListStart}
              onDragListOver={handleDragListOver}
              onDropList={handleDropList}
              draggingListId={draggingListId}
              onMoveList={handleMoveList}
              movingCardIds={movingCardIds}
              showSubtasks={showSubtasks}
              onArchiveList={(listId) => setArchiveDialogListId(listId)}
              onRenameList={handleRenameList}
              onCardMouseEnter={linkHandlers.onCardMouseEnter}
              onCardMouseLeave={linkHandlers.onCardMouseLeave}
            />
          )}
          </ErrorBoundary>

          {/* Link visualization overlay */}
          <LinkOverlay state={linkVisualizationState} containerRef={boardScrollRef} />
        </div>

        {/* Activity Sidebar */}
        {showActivitySidebar && (
          <div className="w-80 border-l border-border overflow-y-auto bg-card relative z-10">
            <ActivitySidebar entries={activityEntries} isLoading={activityLoading} onClose={() => setShowActivitySidebar(false)} />
          </div>
        )}
      </div>
      )}

      {/* Keyboard card move overlay */}
      {keyboardMoveCard && (
        <KeyboardCardMove
          isActive={true}
          cardTitle={keyboardMoveCard.title}
          currentListId={keyboardMoveCard.listId}
          lists={listOptions}
          onMove={handleKeyboardMove}
          onCancel={() => setKeyboardMoveCardId(null)}
        />
      )}

      {/* WebSocket connection status — hide during initial connection before board data loads */}
      <ConnectionStatus state={connectionState} onReconnect={handleReconnect} />

      {/* Stale board toast — shown when remote changes arrive while user is editing */}
      {showStaleToast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg"
        >
          <span className="text-sm">Board updated by another user</span>
          <button
            type="button"
            onClick={() => {
              setShowStaleToast(false);
              if (staleToastTimerRef.current) clearTimeout(staleToastTimerRef.current);
              fetchBoard(boardId);
            }}
            className="text-sm font-medium px-3 py-1 rounded bg-white/20 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setShowStaleToast(false);
              if (staleToastTimerRef.current) clearTimeout(staleToastTimerRef.current);
            }}
            aria-label="Dismiss notification"
            className="text-white/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
          >
            ✕
          </button>
        </div>
      )}

      {/* Archived Items Panel */}
      {showArchivedItems && (
        <ArchivedItemsPanel
          boardId={boardId}
          archivedSwimlanes={state.swimlanes.filter((s) => s.archived)}
          onClose={() => setShowArchivedItems(false)}
          onRestore={async (cardId, listId, swimlaneId, title) => {
            await restoreCard(boardId, cardId, listId, swimlaneId, title);
          }}
          onRestoreSwimlane={async (swimlaneId) => {
            await api.put(`/v1/boards/${boardId}/swimlanes/${swimlaneId}`, { archived: false });
            fetchBoard(boardId);
          }}
        />
      )}

      {/* Board Settings Panel */}
      {showBoardSettings && state.board && (
        <BoardSettings
          boardId={boardId}
          title={state.board.title}
          description={state.board.description}
          color={state.board.color}
          visibility={state.board.visibility}
          labels={[...state.board.labels]}
          onClose={() => setShowBoardSettings(false)}
          onBoardUpdated={(updates) => {
            // Refresh the board to pick up changes
            fetchBoard(boardId);
          }}
          onBoardArchived={() => {
            navigate('/boards');
          }}
        />
      )}

      {/* Jira Export Dialog */}
      {state.board && (
        <JiraExportDialog
          boardId={boardId}
          boardTitle={state.board.title}
          open={showJiraExport}
          onClose={() => setShowJiraExport(false)}
        />
      )}

      {/* Advanced Search Modal */}
      {state.board && (
        <AdvancedSearchModal
          boardId={boardId}
          open={showAdvancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
          initialQuery={advancedSearchQuery}
          lists={state.lists.map((l) => ({ listId: l.listId, title: l.title }))}
          swimlanes={state.swimlanes.map((s) => ({ swimlaneId: s.swimlaneId, title: s.title }))}
          labels={state.board.labels}
          members={state.board.members}
          onCardClick={handleCardClick}
        />
      )}

      {/* Create Swimlane Dialog */}
      <Dialog open={addingSwimlane} onOpenChange={(open) => { if (!open) handleCreateSwimlaneCancel(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Swimlane</DialogTitle>
            <DialogDescription>Enter a name for the new swimlane.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const input = form.elements.namedItem('swimlane-title') as HTMLInputElement;
              const title = input.value.trim();
              if (!title) return;
              try {
                await handleCreateSwimlaneSubmit(title);
              } catch {
                // error is shown inline
              }
            }}
          >
            <input
              name="swimlane-title"
              type="text"
              placeholder="Swimlane title…"
              autoFocus
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Swimlane title"
            />
            {swimlaneCreateError && (
              <p className="text-xs text-destructive mt-1">{swimlaneCreateError}</p>
            )}
            <DialogFooter className="mt-4">
              <button
                type="button"
                onClick={handleCreateSwimlaneCancel}
                className="px-4 py-2 text-sm rounded-md border border-input hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Add Swimlane
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive List Confirmation Dialog */}
      {archiveDialogListId && (() => {
        const targetList = state.lists.find((l) => l.listId === archiveDialogListId);
        const cardCount = state.cards.filter((c) => c.listId === archiveDialogListId).length;
        const otherLists: ArchiveListOption[] = state.lists
          .filter((l) => l.listId !== archiveDialogListId && !l.archived)
          .sort((a, b) => a.sort - b.sort)
          .map((l) => ({ listId: l.listId, title: l.title }));

        return (
          <ArchiveListDialog
            open={true}
            listTitle={targetList?.title ?? ''}
            cardCount={cardCount}
            otherLists={otherLists}
            onArchiveWithCards={() => {
              archiveList(boardId, archiveDialogListId);
              setArchiveDialogListId(null);
            }}
            onMoveCardsAndArchive={(targetListId) => {
              archiveList(boardId, archiveDialogListId, targetListId);
              setArchiveDialogListId(null);
            }}
            onCancel={() => setArchiveDialogListId(null)}
          />
        );
      })()}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Board layout with swimlanes
// ---------------------------------------------------------------------------

interface BoardWithSwimlanesProps {
  readonly swimlanes: readonly SwimlaneSummary[];
  readonly lists: readonly { listId: string; title: string; color: string; width: number; cardCount: number; wipLimit: number | null; wipEnabled: boolean; wipMode: 'soft' | 'hard' | null }[];
  readonly cards: readonly CardSummary[];
  readonly collapsedSwimlanes: Set<string>;
  readonly onToggleCollapse: (swimlaneId: string) => void;
  readonly onCardClick: (cardId: string) => void;
  readonly onCreateCard: (listId: string, swimlaneId: string) => void;
  readonly onCreateCardSubmit: (title: string) => Promise<void>;
  readonly onCreateCardCancel: () => void;
  readonly addingCardListId: string | null;
  readonly addingCardSwimlaneId: string | null;
  readonly cardCreateError: string | null;
  readonly onDragStart: (card: CardSummary) => void;
  readonly onDragOver: (target: DropTarget) => Promise<void>;
  readonly onDrop: () => Promise<void>;
  readonly onDragCancel: () => void;
  readonly dragState: DragState;
  readonly contrastMode: 'light' | 'dark';
  readonly movingCardIds: Set<string>;
  readonly onCreateSwimlane: () => void;
  readonly onCreateSwimlaneSubmit: (title: string) => Promise<void>;
  readonly onCreateSwimlaneCancel: () => void;
  readonly addingSwimlane: boolean;
  readonly swimlaneCreateError: string | null;
  readonly showSubtasks: boolean;
  readonly onArchiveList: (listId: string) => void;
  readonly onMoveList: (listId: string, direction: 'left' | 'right') => void;
  readonly onRenameList: (listId: string, newTitle: string) => void;
  readonly onRenameSwimlane: (swimlaneId: string, newTitle: string) => void;
  readonly onArchiveSwimlane: (swimlaneId: string) => void;
  readonly onMoveSwimlane: (swimlaneId: string, direction: 'up' | 'down') => void;
  readonly onSetSprintDates: (swimlaneId: string, startDate: string | null, endDate: string | null) => void;
  readonly onCreateList: (swimlaneId?: string) => void;
  readonly onCreateListSubmit: (title: string) => Promise<void>;
  readonly onCreateListCancel: () => void;
  readonly addingList: string | null;
  readonly listCreateError: string | null;
  readonly onCardMouseEnter?: (cardId: string) => void;
  readonly onCardMouseLeave?: () => void;
}

function BoardWithSwimlanes({
  swimlanes,
  lists,
  cards,
  collapsedSwimlanes,
  onToggleCollapse,
  onCardClick,
  onCreateCard,
  onCreateCardSubmit,
  onCreateCardCancel,
  addingCardListId,
  addingCardSwimlaneId,
  cardCreateError,
  onDragStart,
  onDragOver,
  onDrop,
  onDragCancel,
  dragState,
  contrastMode,
  movingCardIds,
  onCreateSwimlane,
  onCreateSwimlaneSubmit,
  onCreateSwimlaneCancel,
  addingSwimlane,
  swimlaneCreateError,
  showSubtasks,
  onArchiveList,
  onMoveList,
  onRenameList,
  onRenameSwimlane,
  onArchiveSwimlane,
  onMoveSwimlane,
  onSetSprintDates,
  onCreateList,
  onCreateListSubmit,
  onCreateListCancel,
  addingList,
  listCreateError,
  onCardMouseEnter,
  onCardMouseLeave,
}: BoardWithSwimlanesProps): React.ReactElement {
  const isLight = contrastMode === 'light';
  const addSwimlaneTextClass = isLight ? 'text-white/80 hover:text-white' : 'text-gray-900/70 hover:text-gray-900';
  const addSwimlaneBgClass = isLight ? 'bg-white/20 hover:bg-white/30' : 'bg-black/10 hover:bg-black/20';
  const addSwimlaneRingClass = isLight ? 'focus:ring-white/50' : 'focus:ring-gray-900/50';

  return (
    <div>
      {swimlanes.map((swimlane, index) => (
        <SwimlaneRow
          key={swimlane.swimlaneId}
          swimlaneId={swimlane.swimlaneId}
          title={swimlane.title}
          color={swimlane.color}
          startDate={swimlane.startDate}
          endDate={swimlane.endDate}
          collapsed={collapsedSwimlanes.has(swimlane.swimlaneId)}
          onToggleCollapse={onToggleCollapse}
          onRename={onRenameSwimlane}
          onArchive={onArchiveSwimlane}
          onMoveUp={index > 0 ? (id) => onMoveSwimlane(id, 'up') : undefined}
          onMoveDown={index < swimlanes.length - 1 ? (id) => onMoveSwimlane(id, 'down') : undefined}
          onSetSprintDates={onSetSprintDates}
        >
          {lists.map((list) => {
            const listCards = cards.filter(
              (c) => c.listId === list.listId && c.swimlaneId === swimlane.swimlaneId
            );
            const isAddingCard = addingCardListId === list.listId && addingCardSwimlaneId === swimlane.swimlaneId;
            return (
              <ListColumn
                key={list.listId}
                listId={list.listId}
                title={list.title}
                color={list.color}
                width={list.width}
                cardCount={listCards.length}
                wipLimit={list.wipLimit}
                wipEnabled={list.wipEnabled}
                wipMode={list.wipMode}
                onMoveLeft={() => onMoveList(list.listId, 'left')}
                onMoveRight={() => onMoveList(list.listId, 'right')}
                isFirst={lists.indexOf(list) === 0}
                isLast={lists.indexOf(list) === lists.length - 1}
                onArchive={() => onArchiveList(list.listId)}
                onRenameList={onRenameList}
                onHeaderDragOver={(e) => {
                  if (e.dataTransfer.types.includes('application/x-list-drag')) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  onDragOver({ listId: list.listId, swimlaneId: swimlane.swimlaneId, sort: 0 });
                }}
                onHeaderDrop={(e) => {
                  if (e.dataTransfer.types.includes('application/x-list-drag')) return;
                  e.preventDefault();
                  onDrop();
                }}
                onCardDragOver={(e) => {
                  if (e.dataTransfer.types.includes('application/x-list-drag')) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  onDragOver({ listId: list.listId, swimlaneId: swimlane.swimlaneId, sort: -1 });
                }}
                onCardDrop={(e) => {
                  if (e.dataTransfer.types.includes('application/x-list-drag')) return;
                  e.preventDefault();
                  onDrop();
                }}
              >
                {orderCardsWithSubtasks(listCards, showSubtasks).map((card, idx, arr) => (
                  <DraggableCard
                    key={card.cardId}
                    card={card}
                    onCardClick={onCardClick}
                    onDragStart={onDragStart}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onDragCancel={onDragCancel}
                    dragState={dragState}
                    targetListId={list.listId}
                    targetSwimlaneId={swimlane.swimlaneId}
                    prevSort={idx > 0 ? arr[idx - 1]!.sort : null}
                    nextSort={idx < arr.length - 1 ? arr[idx + 1]!.sort : null}
                    isMoving={movingCardIds.has(card.cardId)}
                    isNested={showSubtasks && card.isSubtask}
                    onCardMouseEnter={onCardMouseEnter}
                    onCardMouseLeave={onCardMouseLeave}
                  />
                ))}
                {isAddingCard ? (
                  <InlineCreationForm
                    placeholder="Enter card title…"
                    submitLabel="Add Card"
                    onSubmit={onCreateCardSubmit}
                    onCancel={onCreateCardCancel}
                    error={cardCreateError}
                  />
                ) : (
                  <AddCardButton
                    listId={list.listId}
                    swimlaneId={swimlane.swimlaneId}
                    onCreateCard={onCreateCard}
                    contrastMode={contrastMode}
                  />
                )}
              </ListColumn>
            );
          })}
          {/* Add List — inline at end of columns, only show form in the swimlane where clicked */}
          {(() => {
            const isActiveForm = addingList === swimlane.swimlaneId;
            if (addingList && !isActiveForm) return null;
            return (
              <div className="flex-shrink-0" style={{ width: '272px', minWidth: '272px' }}>
                {isActiveForm ? (
                  <div className="rounded-md bg-card/80 backdrop-blur-sm p-3 border border-border/50">
                    <InlineCreationForm
                      placeholder="Enter list title…"
                      submitLabel="Add List"
                      onSubmit={onCreateListSubmit}
                      onCancel={onCreateListCancel}
                      error={listCreateError}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onCreateList(swimlane.swimlaneId)}
                    className="w-full rounded-lg bg-card/40 backdrop-blur-sm p-3 text-sm text-muted-foreground hover:bg-card/60 hover:text-card-foreground border border-dashed border-border/50 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label="Add another list"
                  >
                    + Add List
                  </button>
                )}
              </div>
            );
          })()}
        </SwimlaneRow>
      ))}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Board flat layout (single swimlane)
// ---------------------------------------------------------------------------

interface BoardFlatLayoutProps {
  readonly lists: readonly { listId: string; title: string; color: string; width: number; cardCount: number; wipLimit: number | null; wipEnabled: boolean; wipMode: 'soft' | 'hard' | null; sort: number }[];
  readonly cards: readonly CardSummary[];
  readonly swimlaneId: string;
  readonly onCardClick: (cardId: string) => void;
  readonly onCreateCard: (listId: string, swimlaneId: string) => void;
  readonly onCreateCardSubmit: (title: string) => Promise<void>;
  readonly onCreateCardCancel: () => void;
  readonly addingCardListId: string | null;
  readonly addingCardSwimlaneId: string | null;
  readonly cardCreateError: string | null;
  readonly onCreateList: () => void;
  readonly onCreateListSubmit: (title: string) => Promise<void>;
  readonly onCreateListCancel: () => void;
  readonly addingList: string | null;
  readonly listCreateError: string | null;
  readonly onDragStart: (card: CardSummary) => void;
  readonly onDragOver: (target: DropTarget) => Promise<void>;
  readonly onDrop: () => Promise<void>;
  readonly onDragCancel: () => void;
  readonly dragState: DragState;
  readonly contrastMode: 'light' | 'dark';
  readonly onDragListStart: (listId: string) => void;
  readonly onDragListOver: (e: React.DragEvent, targetListId: string) => void;
  readonly onDropList: (targetListId: string) => void;
  readonly draggingListId: string | null;
  readonly onMoveList: (listId: string, direction: 'left' | 'right') => void;
  readonly movingCardIds: Set<string>;
  readonly showSubtasks: boolean;
  readonly onArchiveList: (listId: string) => void;
  readonly onRenameList: (listId: string, newTitle: string) => void;
  readonly onCardMouseEnter?: (cardId: string) => void;
  readonly onCardMouseLeave?: () => void;
}

function BoardFlatLayout({
  lists,
  cards,
  swimlaneId,
  onCardClick,
  onCreateCard,
  onCreateCardSubmit,
  onCreateCardCancel,
  addingCardListId,
  addingCardSwimlaneId,
  cardCreateError,
  onCreateList,
  onCreateListSubmit,
  onCreateListCancel,
  addingList,
  listCreateError,
  onDragStart,
  onDragOver,
  onDrop,
  onDragCancel,
  dragState,
  contrastMode,
  onDragListStart,
  onDragListOver,
  onDropList,
  draggingListId,
  onMoveList,
  movingCardIds,
  showSubtasks,
  onArchiveList,
  onRenameList,
  onCardMouseEnter,
  onCardMouseLeave,
}: BoardFlatLayoutProps): React.ReactElement {
  const isLight = contrastMode === 'light';
  const addListTextClass = isLight ? 'text-white/80 hover:text-white' : 'text-gray-900/70 hover:text-gray-900';
  const addListBgClass = isLight ? 'bg-white/20 hover:bg-white/30' : 'bg-black/10 hover:bg-black/20';
  const addListRingClass = isLight ? 'focus:ring-white/50' : 'focus:ring-gray-900/50';

  return (
    <div className="flex gap-3 h-full w-max min-w-full pb-3">
      {lists.map((list) => {
        const listCards = cards.filter((c) => c.listId === list.listId);
        const isAddingCard = addingCardListId === list.listId && addingCardSwimlaneId === swimlaneId;
        const isDraggingList = draggingListId === list.listId;
        return (
          <div
            key={list.listId}
            className={`flex-shrink-0 transition-opacity ${isDraggingList ? 'opacity-50 border-2 border-dashed border-border rounded-lg' : ''}`}
            style={{ width: `${list.width}px`, minWidth: `${list.width}px` }}
            onDragOver={(e) => {
              if (draggingListId && draggingListId !== list.listId) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                onDragListOver(e, list.listId);
              }
            }}
            onDrop={(e) => {
              if (draggingListId && draggingListId !== list.listId) {
                e.preventDefault();
                e.stopPropagation();
                onDropList(list.listId);
              }
            }}
          >
            <ListColumn
              listId={list.listId}
              title={list.title}
              color={list.color}
              width={list.width}
              cardCount={listCards.length}
              wipLimit={list.wipLimit}
              wipEnabled={list.wipEnabled}
              wipMode={list.wipMode}
              draggable
              onDragStart={() => onDragListStart(list.listId)}
              onMoveLeft={() => onMoveList(list.listId, 'left')}
              onMoveRight={() => onMoveList(list.listId, 'right')}
              onArchive={() => onArchiveList(list.listId)}
              onRenameList={onRenameList}
              isFirst={lists.findIndex(l => l.listId === list.listId) === 0}
              isLast={lists.findIndex(l => l.listId === list.listId) === lists.length - 1}
              onHeaderDragOver={(e) => {
                if (e.dataTransfer.types.includes('application/x-list-drag')) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                onDragOver({ listId: list.listId, swimlaneId, sort: 0 });
              }}
              onHeaderDrop={(e) => {
                if (e.dataTransfer.types.includes('application/x-list-drag')) return;
                e.preventDefault();
                onDrop();
              }}
              onCardDragOver={(e) => {
                if (e.dataTransfer.types.includes('application/x-list-drag')) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                onDragOver({ listId: list.listId, swimlaneId, sort: -1 });
              }}
              onCardDrop={(e) => {
                if (e.dataTransfer.types.includes('application/x-list-drag')) return;
                e.preventDefault();
                onDrop();
              }}
            >
              {orderCardsWithSubtasks(listCards, showSubtasks).map((card, idx, arr) => (
                <DraggableCard
                  key={card.cardId}
                  card={card}
                  onCardClick={onCardClick}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onDragCancel={onDragCancel}
                  dragState={dragState}
                  targetListId={list.listId}
                  targetSwimlaneId={swimlaneId}
                  prevSort={idx > 0 ? arr[idx - 1]!.sort : null}
                  nextSort={idx < arr.length - 1 ? arr[idx + 1]!.sort : null}
                  isMoving={movingCardIds.has(card.cardId)}
                  isNested={showSubtasks && card.isSubtask}
                  onCardMouseEnter={onCardMouseEnter}
                  onCardMouseLeave={onCardMouseLeave}
                />
              ))}
              {isAddingCard ? (
                <InlineCreationForm
                  placeholder="Enter card title…"
                  submitLabel="Add Card"
                  onSubmit={onCreateCardSubmit}
                  onCancel={onCreateCardCancel}
                  error={cardCreateError}
                />
              ) : (
                <AddCardButton
                  listId={list.listId}
                  swimlaneId={swimlaneId}
                  onCreateCard={onCreateCard}
                  contrastMode={contrastMode}
                />
              )}
            </ListColumn>
          </div>
        );
      })}

      {/* Add List button / form */}
      <div className="flex-shrink-0" style={{ width: '272px', minWidth: '272px' }}>
        {addingList ? (
          <div className="rounded-md bg-card p-3">
            <InlineCreationForm
              placeholder="Enter list title…"
              submitLabel="Add List"
              onSubmit={onCreateListSubmit}
              onCancel={onCreateListCancel}
              error={listCreateError}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={onCreateList}
            className={`w-full rounded-md ${addListBgClass} p-3 text-sm ${addListTextClass} transition-colors focus:outline-none focus:ring-2 ${addListRingClass}`}
            aria-label="Add another list"
          >
            + Add List
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card ordering helper — nests subtasks below their parent
// ---------------------------------------------------------------------------

/**
 * Orders cards so subtasks appear directly below their parent card.
 * When showSubtasks is off, subtasks are already filtered out so this is a no-op.
 * When showSubtasks is on, parent cards render first, then their subtask children
 * are placed immediately after them.
 */
function orderCardsWithSubtasks(listCards: readonly CardSummary[], showSubtasks: boolean): CardSummary[] {
  if (!showSubtasks) {
    return [...listCards].sort((a, b) => a.sort - b.sort);
  }

  const parentCards = [...listCards.filter((c) => !c.isSubtask)].sort((a, b) => a.sort - b.sort);
  const subtaskCards = [...listCards.filter((c) => c.isSubtask)].sort((a, b) => a.sort - b.sort);

  const ordered: CardSummary[] = [];
  for (const parent of parentCards) {
    ordered.push(parent);
    const children = subtaskCards.filter((c) => c.parentCardId === parent.cardId);
    ordered.push(...children);
  }

  // Include orphan subtasks (parent not in this list) at the end
  const placedSubtaskIds = new Set(ordered.filter((c) => c.isSubtask).map((c) => c.cardId));
  const orphans = subtaskCards.filter((c) => !placedSubtaskIds.has(c.cardId));
  ordered.push(...orphans);

  return ordered;
}

// ---------------------------------------------------------------------------
// Draggable card wrapper
// ---------------------------------------------------------------------------

interface DraggableCardProps {
  readonly card: CardSummary;
  readonly onCardClick: (cardId: string) => void;
  readonly onDragStart: (card: CardSummary) => void;
  readonly onDragOver: (target: DropTarget) => Promise<void>;
  readonly onDrop: () => Promise<void>;
  readonly onDragCancel: () => void;
  readonly dragState: DragState;
  readonly targetListId: string;
  readonly targetSwimlaneId: string;
  readonly prevSort: number | null;
  readonly nextSort: number | null;
  readonly isMoving?: boolean;
  readonly isNested?: boolean;
  readonly onCardMouseEnter?: (cardId: string) => void;
  readonly onCardMouseLeave?: () => void;
}

function DraggableCard({
  card,
  onCardClick,
  onDragStart,
  onDragOver,
  onDrop,
  onDragCancel,
  dragState,
  targetListId,
  targetSwimlaneId,
  prevSort,
  nextSort,
  isMoving = false,
  isNested = false,
  onCardMouseEnter,
  onCardMouseLeave,
}: DraggableCardProps): React.ReactElement {
  const isDragging = dragState.isDragging && dragState.dragItem?.id === card.cardId;
  const cardRef = React.useRef<HTMLDivElement>(null);
  const { state, moveCard } = useActiveBoard();
  const { boardId } = useParams<{ boardId: string }>();
  const [showMoveModal, setShowMoveModal] = React.useState(false);

  const handleDragStartEvent = (e: React.DragEvent) => {
    if (isMoving) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', card.cardId);
    onDragStart(card);
  };

  const handleDragOverEvent = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    // Skip if dragging over ourselves
    if (dragState.isDragging && dragState.dragItem?.id === card.cardId) return;

    // Calculate sort based on cursor position within the card:
    // Top half = insert before this card (midpoint between prev and this card)
    // Bottom half = insert after this card (midpoint between this card and next)
    const rect = cardRef.current?.getBoundingClientRect();
    let sort: number;
    if (rect && e.clientY < rect.top + rect.height / 2) {
      // Insert before this card
      const prev = prevSort ?? 0;
      sort = Math.floor((prev + card.sort) / 2);
      if (sort === prev || sort === card.sort) sort = prev + 1;
    } else {
      // Insert after this card
      const next = nextSort ?? card.sort + 2048;
      sort = Math.floor((card.sort + next) / 2);
      if (sort === card.sort || sort === next) sort = card.sort + 1;
    }

    onDragOver({ listId: targetListId, swimlaneId: targetSwimlaneId, sort });
  };

  const handleDropEvent = (e: React.DragEvent) => {
    // Only handle card drops, not list drops
    if (e.dataTransfer.types.includes('application/x-list-drag')) return;
    e.preventDefault();
    e.stopPropagation();
    onDrop();
  };

  const handleDragEnd = () => {
    if (dragState.isDragging) {
      onDragCancel();
    }
  };

  return (
    <div
      ref={cardRef}
      data-card-id={card.cardId}
      draggable={!isMoving}
      onDragStart={handleDragStartEvent}
      onDragOver={handleDragOverEvent}
      onDrop={handleDropEvent}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => onCardMouseEnter?.(card.cardId)}
      onMouseLeave={() => onCardMouseLeave?.()}
      className={`relative group ${isDragging ? 'opacity-50' : ''} ${isMoving ? 'opacity-50 pointer-events-none' : ''} ${isNested ? 'ml-6 border-l-2 border-primary/30' : ''} transition-opacity duration-200`}
      aria-grabbed={isDragging}
      aria-busy={isMoving}
    >
      <MiniCard
        cardId={card.cardId}
        cardNumber={card.cardNumber}
        title={card.title}
        labels={(card.labels ?? []).map((label) => {
          if (typeof label === 'string') {
            return { name: label, color: '#6b7280' };
          }
          const obj = label as unknown as { name?: string; color?: string; labelId?: string };
          return { name: obj.name ?? obj.labelId ?? '', color: obj.color ?? '#6b7280' };
        })}
        memberAvatars={[...(card.memberAvatars ?? [])]}
        memberNames={(card.members ?? []).map((m) => m.displayName)}
        dueDate={card.dueDate}
        coverImage={card.coverImage}
        checklistProgress={card.checklistProgress}
        subtaskProgress={card.subtaskProgress}
        customFields={card.customFields}
        customFieldDefinitions={state.customFieldDefinitions}
        description={card.description}
        showDescriptionOnCard={card.showDescriptionOnCard}
        onClick={onCardClick}
      />

      {/* Move button — visible on hover */}
      {!isMoving && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowMoveModal(true); }}
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded bg-card/80 text-muted-foreground hover:text-foreground hover:bg-card transition-opacity text-xs"
          aria-label={`Move card: ${card.title}`}
          title="Move card"
        >
          ↗
        </button>
      )}

      {showMoveModal && (
        <MoveCardModal
          boardId={boardId!}
          cardId={card.cardId}
          cardTitle={card.title}
          currentListId={card.listId}
          currentSwimlaneId={card.swimlaneId}
          open={showMoveModal}
          onOpenChange={setShowMoveModal}
          onMoveConfirmed={async (listId, swimlaneId) => {
            setShowMoveModal(false);
            await moveCard(boardId!, { cardId: card.cardId, targetListId: listId, targetSwimlaneId: swimlaneId });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add card button
// ---------------------------------------------------------------------------

interface AddCardButtonProps {
  readonly listId: string;
  readonly swimlaneId: string;
  readonly onCreateCard: (listId: string, swimlaneId: string) => void;
  readonly contrastMode?: 'light' | 'dark';
}

function AddCardButton({ listId, swimlaneId, onCreateCard, contrastMode = 'light' }: AddCardButtonProps): React.ReactElement {
  const isLight = contrastMode === 'light';
  const textClass = isLight ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-900/60 hover:text-gray-900 hover:bg-black/10';
  const ringClass = isLight ? 'focus:ring-white/50' : 'focus:ring-gray-900/50';

  return (
    <button
      onClick={() => onCreateCard(listId, swimlaneId)}
      aria-label="Add card"
      className={`w-full mt-1 px-3 py-2 text-sm ${textClass} rounded transition-colors focus:outline-none focus:ring-2 ${ringClass}`}
    >
      + Add card
    </button>
  );
}


// ---------------------------------------------------------------------------
// WebSocket event type guard
// ---------------------------------------------------------------------------

const VALID_EVENT_TYPES = new Set([
  'CardCreated',
  'CardMoved',
  'CardUpdated',
  'ListCreated',
  'ListUpdated',
  'SwimlaneCreated',
  'SwimlaneUpdated',
  'MemberAdded',
  'CustomfieldCreated',
  'CustomfieldUpdated',
  'CustomfieldDeleted',
]);

function isWebSocketEvent(data: Record<string, unknown>): data is WebSocketEvent {
  return typeof data.eventType === 'string' && VALID_EVENT_TYPES.has(data.eventType);
}
